/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Xaero Zero - xaerozero@icloud.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {Subject} from 'rxjs';
import {StageService, ModeService, UserService} from '../../../../../services';
import {ReportUserType, RestError, Stage, User, HistoryStage} from '../../../../../model';
import {ReportProvider, UserProvider} from '../../../../../providers';
import {PresetValue} from '../../../../../model/preset-value';
import {SearchableSelection, SearchableSelectionType} from '../../../../../model/interface/searchable.selection';
import {AssigneeType, DelayType, ErrorType, Report, ReportUsers, StageChangeRequest} from '../../../../../model/report';
import {CrmAlertComponent} from '../../../../fields';
import { ReportService } from 'src/app/services/report.service';
import { SystemType } from 'src/app/model/enums/system.type';
import * as moment from 'moment';
import { LogService } from 'src/app/services';


@Component({
  selector: 'report-stage-modal',
  templateUrl: './report-stage.modal.html',
  styleUrls: ['./report-stage.modal.scss']
})
export class ReportStageModal implements OnInit, OnDestroy {
  @ViewChild('alert') alert: CrmAlertComponent;

  subject: Subject<HistoryStage>;
  subscription: any;
  reportId: string;
  nextStageId: string;
  stagesList: Stage[];
  private stagesSub: any;
  userList: ReportUsers;
  stageRequest: StageChangeRequest = new StageChangeRequest();
  userTypes: PresetValue[] = [];
  auto = false;
  disabledInput = false;
  assignToInputValue: any;
  assigneeModal: BsModalRef;
  isChangedAssignee = false;
  fileLocation: string;

  SystemType = SystemType;
  modeSub: any;
  currentMode: SystemType;
  assignToMe: boolean;

  userSub: any;

  currentUser: User;
  currentReport:Report;

  delays: PresetValue[] = [
    PresetValue.create('Reliant Delay', DelayType.RELIANT),
    PresetValue.create('Client Delay', DelayType.CLIENT)
  ];

  errors: PresetValue[] = [
    PresetValue.create('No Errors', ErrorType.NO_ERRORS),
    PresetValue.create('Minor Errors', ErrorType.MINOR_ERRORS),
    PresetValue.create('Major Errors', ErrorType.MAJOR_ERRORS)
  ];

  constructor(
    public bsModalRef: BsModalRef,
    private modalService: BsModalService,
    public stageService: StageService,
    public userProvider: UserProvider,
    private reportProvider: ReportProvider,
    private reportService: ReportService,
    private modeService: ModeService,
    private userService: UserService,
    private logService : LogService
  ) {}

  ngOnInit(): void {
    // Fix for when user clicks `ESC` instead of clicking, no Event was firing
    this.subscription = this.modalService.onHide.subscribe((reason: string) => {
      if (reason === 'esc')
        this.onCancel();
    });

    this.stagesSub = this.stageService.stages$.subscribe(states => this.stagesList = states);

    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);

    this.userSub = this.userService.user$.subscribe(user => {
      if(user){
        this.currentUser = user;
        if (this.currentUser.hasPosition('RESEARCH_ANALYST'))
          this.assignToMe = true;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.stagesSub)
      this.stagesSub.unsubscribe();
    if (this.subscription)
      this.subscription.unsubscribe();

    if(this.modeSub)
      this.modeSub.unsubscribe();

    if(this.userSub)
      this.userSub.unsubscribe();
  }

  get nextStage(): Stage {
    if (this.stagesList)
      return this.stagesList.filter(stage => stage.ID === this.nextStageId)[0];
    else return null;
  }

  onConfirm(): void {
    this.alert.hide();

    if (!this.stageRequest.ID && !this.nextStageId) {
      const err = new RestError();
      err.txt = 'Please select all parameters';
      this.alert.show(err);
      return;
    }

    if (!this.stageRequest.ID)
      this.stageRequest.ID = this.nextStageId;

    if(this.assignToMe && this.currentUser){
      this.stageRequest.ASSIGNEE_TYPE = AssigneeType.USER;
      this.stageRequest.ASSIGNEE_ID = this.currentUser.ID;
      this.stageRequest.ASSIGNEE_TYPE_USER = null;
    }

    this.reportProvider.setStage(this.reportId, this.stageRequest)
      .subscribe(data => {
        if (data.success) {
          // Update VALUE_ASSESSMENT on currentReport if it was set
          if (this.currentReport && this.stageRequest.VALUE_ASSESSMENT !== undefined) {
            this.currentReport.VALUE_ASSESSMENT = this.stageRequest.VALUE_ASSESSMENT;
          }
          this.bsModalRef.hide();
          this.subject.next(HistoryStage.parse(data.result));
          this.subject.complete();
        } else this.alert.show(data.error);
      }, err => {
        this.logService.error(err);
        this.alert.showError(err.message);
      });
  }

  onCancel(): void {
    this.bsModalRef.hide();
    this.subject.next(null);
    this.subject.complete();
  }

  assignToSelected(value: SearchableSelection): void {
    if (value) {
      if (value.TYPE === SearchableSelectionType.VALUE) {
        this.stageRequest.ASSIGNEE_TYPE = AssigneeType.USER;
        this.stageRequest.ASSIGNEE_ID = (<User>value.VALUE).ID;
        this.stageRequest.ASSIGNEE_TYPE_USER = null;
      } else if (value.TYPE === SearchableSelectionType.SCRIPT) {
        this.stageRequest.ASSIGNEE_TYPE = AssigneeType.ROLE;
        this.stageRequest.ASSIGNEE_TYPE_USER = value.VALUE;
        this.stageRequest.ASSIGNEE_ID = null;
      }
      this.assignToInputValue = value.VALUE;
    } else { // Clear
      this.stageRequest.ASSIGNEE_TYPE = null;
      this.stageRequest.ASSIGNEE_ID = null;
      this.stageRequest.ASSIGNEE_TYPE_USER = null;
      this.assignToInputValue = null;
      this.assignToMe = false;
    }
  }

  stageSelected(value: SearchableSelection): void {
    if (value) {
      this.stageRequest.ID = value.VALUE;
      this.getReportUser(this.stageRequest.ID);
    } else {
      this.auto = false;
      this.stageRequest.ID = null;
      this.stageRequest.ASSIGNEE_TYPE = null;
      this.stageRequest.ASSIGNEE_ID = null;
      this.stageRequest.ASSIGNEE_TYPE_USER = null;
      this.assignToInputValue = null;
      this.assignToMe = false;
    }
  }

  setReportUsers(): void {
    if(this.currentMode === SystemType.EA){
      if (this.userList?.EQUIPMENT_PROCESSOR && this.userList.EQUIPMENT_PROCESSOR.USER)
        this.userTypes.push(PresetValue.create('Equipment Processor - ' + this.userList.EQUIPMENT_PROCESSOR.USER.getName(), ReportUserType.EQUIPMENT_PROCESSOR));
      if (this.userList?.EQUIPMENT_RESEARCH_ANALYST && this.userList.EQUIPMENT_RESEARCH_ANALYST.USER)
        this.userTypes.push(PresetValue.create('Equipment Research Analyst - ' + this.userList.EQUIPMENT_RESEARCH_ANALYST.USER.getName(), ReportUserType.EQUIPMENT_RESEARCH_ANALYST));
      if (this.userList?.EQUIPMENT_APPRAISER && this.userList.EQUIPMENT_APPRAISER.USER)
        this.userTypes.push(PresetValue.create('Equipment Appraiser - ' + this.userList.EQUIPMENT_APPRAISER.USER.getName(), ReportUserType.EQUIPMENT_APPRAISER));
      if (this.userList?.EQUIPMENT_REVIEWER && this.userList.EQUIPMENT_REVIEWER.USER)
        this.userTypes.push(PresetValue.create('Equipment Reviewer - ' + this.userList.EQUIPMENT_REVIEWER.USER.getName(), ReportUserType.EQUIPMENT_REVIEWER));
      if (this.userList?.EQUIPMENT_SUBMITTER && this.userList.EQUIPMENT_SUBMITTER.USER)
        this.userTypes.push(PresetValue.create('Equipment Submitter - ' + this.userList.EQUIPMENT_SUBMITTER.USER.getName(), ReportUserType.EQUIPMENT_SUBMITTER));
    }
    else {
      if (this.userList?.PROCESSOR && this.userList.PROCESSOR.USER)
        this.userTypes.push(PresetValue.create('Processor - ' + this.userList.PROCESSOR.USER.getName(), ReportUserType.PROCESSOR));
      if (this.userList?.RESEARCH_ANALYST && this.userList.RESEARCH_ANALYST.USER)
        this.userTypes.push(PresetValue.create('Research Analyst - ' + this.userList.RESEARCH_ANALYST.USER.getName(), ReportUserType.RESEARCH_ANALYST));
      if (this.userList?.FINANCIAL_ANALYST && this.userList.FINANCIAL_ANALYST.USER)
        this.userTypes.push(PresetValue.create('Financial Analyst - ' + this.userList.FINANCIAL_ANALYST.USER.getName(), ReportUserType.FINANCIAL_ANALYST));
      if (this.userList?.SENIOR_ANALYST && this.userList.SENIOR_ANALYST.USER)
        this.userTypes.push(PresetValue.create('Senior Analyst - ' + this.userList.SENIOR_ANALYST.USER.getName(), ReportUserType.SENIOR_ANALYST));


      this.userTypes.push(PresetValue.create('Senior Appraiser', ReportUserType.SENIOR_APPRAISER));
      this.userTypes.push(PresetValue.create('Senior Reviewer', ReportUserType.SENIOR_REVIEWER));
    }

    if (this.userList?.OTHER && this.userList.OTHER.USER)
      this.userTypes.push(PresetValue.create('Other - ' + this.userList.OTHER.USER.getName(), ReportUserType.OTHER));
  }

  validateInput(): boolean {
    if (!this.stageRequest.ID) {
      return false;
    }

    if(this.isSubmitted()){
      if(!this.stageRequest.RATING){
        return false;
      }
      if(!this.stageRequest.ERROR){
        return false;
      }
    }

    if(this.isOverDue()){
      if(!this.stageRequest.NOTES){
        return false;
      }

      if(!this.stageRequest.DELAY){
        return false
      }
    }

    return true;
  }

  getReportUser(id?: string): void {
    const nextId = id ? id : this.nextStageId;
    this.stageRequest = this.reportService.getReportStageRequest(nextId, this.userList);
    if (this.stageRequest.ASSIGNEE_TYPE_USER) {
      this.auto = true;
      this.assignToInputValue = this.stageRequest.ASSIGNEE_TYPE_USER;
    } else {
      this.auto  = false;
    }
  }

  setStageList(): void {
    if (this.stagesList) {
      const index = this.stagesList.findIndex(s => s.ID === this.nextStageId);
      if (index) {
        this.stagesList.splice(0, 0, this.stagesList.splice(index, 1)[0]);
      }
    }
  }

  onSelectedAssignee(value: any): void {
    if(value){
      this.reportProvider.setReportUser(this.reportId, this.assignToInputValue, value ? (<User>value.VALUE).ID : null)
        .subscribe(data => {
          if (data.success) {
            if (data.result){
              this.userList[this.assignToInputValue].USER = User.parse(value.VALUE);
              this.userTypes = [];
              this.setReportUsers();
              this.isChangedAssignee = false;
            }
          } else this.logService.error(JSON.stringify(data, null, 2));
        });
    }

  }

  isSubmitter(): boolean {
    if (this.stageRequest.ID === 'SUBR' || this.stageRequest.ID === 'SBMT')
      return true;

    return false;
  }

  isSubmitted(): boolean {
    let submitID = 'SUBR';

    if(this.currentMode == SystemType.EA)
      submitID = 'EAPS';
    if (this.stageRequest.ID === submitID)
      return true;

    return false;
  }

  onAssignToMeValueChange(value: any): void {
    if(value != true){
      this.stageRequest.ASSIGNEE_TYPE = null;
      this.stageRequest.ASSIGNEE_ID = null;
      this.stageRequest.ASSIGNEE_TYPE_USER = null;
    }

    this.assignToMe = value;
    this.assignToInputValue = null;
  }


  onRatingSet(rating: any): void {
    this.stageRequest.RATING = rating;
  }

  isOverDue(): boolean{
    if(this.stageRequest.ID == 'SUBR' || this.stageRequest.ID == 'EAPS'){
      if(this.currentReport?.DATE_DUE_EXPIRED)
        if(this.currentReport.DELAYS?.length == 0)
          return true;
    }

    return false;
  }

  onDelaySelected(value: any): void {
    this.stageRequest.DELAY = value != null ?  value.VALUE: null;
  }
  onErrorSelected(value: any): void {
    this.stageRequest.ERROR = value != null ?  value.VALUE: null;
  }

  onValueAssessmentChange(value: any): void {
    this.stageRequest.VALUE_ASSESSMENT = value;
  }
}
